"use client";

/**
 * Interstitial de mayoría de edad (18+) para secciones con bebidas alcohólicas
 * (categoría "Vinos y Licores" y sus productos). Replica el diseño aprobado:
 * círculo "18+", título, tres bloques de aviso, mascota/candado y botones.
 *
 * La confirmación se recuerda en localStorage para no repetir el gate.
 */
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Wine, ShieldCheck, ShoppingBag, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AGE_VERIFIED_STORAGE_KEY } from "@/lib/taxonomy/age-restricted";

export function AgeGate() {
  const router = useRouter();
  // null = aún sin determinar (evita flash en SSR/hidratación).
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      setVerified(localStorage.getItem(AGE_VERIFIED_STORAGE_KEY) === "1");
    } catch {
      setVerified(false);
    }
  }, []);

  // Bloquea el scroll del body mientras el gate está visible.
  useEffect(() => {
    if (verified === false) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [verified]);

  if (verified !== false) return null;

  function confirm() {
    try {
      localStorage.setItem(AGE_VERIFIED_STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setVerified(true);
  }

  function cancel() {
    router.push("/");
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Acceso exclusivo para mayores de edad"
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs"
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="p-6 sm:p-8">
          {/* 18+ badge */}
          <div className="mb-5 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-red-600 text-xl font-extrabold text-red-600">
              18+
            </div>
          </div>

          <h2 className="text-center text-2xl font-extrabold leading-tight text-zinc-900 dark:text-white">
            Acceso exclusivo
            <br />
            para <span className="text-red-600">mayores de edad</span>
          </h2>

          <div className="my-5 flex items-center justify-center gap-3 text-red-600">
            <span className="h-px w-12 bg-zinc-200 dark:bg-zinc-700" />
            <Lock className="h-5 w-5" />
            <span className="h-px w-12 bg-zinc-200 dark:bg-zinc-700" />
          </div>

          <ul className="space-y-4 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
            <li className="flex gap-3">
              <Wine className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <span>
                La sección que estás por consultar incluye bebidas alcohólicas y
                está dirigida exclusivamente a personas{" "}
                <strong>mayores de edad</strong>.
              </span>
            </li>
            <li className="flex gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <span>
                SuCom es un comparador de precios. Promovemos el consumo
                responsable y apoyamos el cumplimiento de la legislación vigente.{" "}
                <strong>Evita el exceso.</strong>
              </span>
            </li>
            <li className="flex gap-3">
              <ShoppingBag className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <span>
                La venta y entrega de estos productos son responsabilidad de cada
                tienda, quienes podrán verificar nuevamente la mayoría de edad al
                momento de la compra o entrega.
              </span>
            </li>
          </ul>

          <div className="mt-7 space-y-3">
            <Button
              onClick={confirm}
              className="h-12 w-full bg-red-600 text-base font-semibold text-white hover:bg-red-700"
            >
              Tengo más de 18 años
            </Button>
            <Button
              onClick={cancel}
              variant="outline"
              className="h-12 w-full border-red-600 text-base font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
